import React, { useEffect, useMemo } from 'react';
import { Grid, Typography, Box, Divider, Skeleton } from '@mui/material';
import FilterBar from './filterBar';
import WebMetricsInfo from './webMetricsInfo';
import { HighchartBarChart, AreaChart } from '../../shared';
import { useDispatch, useSelector } from 'react-redux';
import {
    getWebMatrixCostDataFalse,
    getWebMatrixFalse,
    getWebMatrixGeographyDataFalse,
    getWebMatrixMultiUsageFalse,
} from '../../../redux/thunks/webMatrixThunks';
import { actions as webMatrixActions } from '../../../redux/reducers/webMatrixReducer';
import {
    CREATIVE_NAME_CHART_OPTIONS,
    CREATIVE_NAME_CHART_SERIES,
} from './constants';
import WebMetricsNote from './webMetricsNotes/webMetricsNote';
import { hideBounceRateCompanies } from '../../../constants';
import MapBreakDown from './mapBreakDown';
import CPCChart from './CPCChart';
import { getDigitalEngagement } from '../../../redux/thunks/engagementThunks';
import { getCompanyConfigurationByTag } from '../../../redux/thunks/companyThunks';
import _ from 'lodash';
const NEW_VS_RETURNING_FROM_WEB_MATRIX = [67, 98];


const showUsersChart = [140, 66, 64, 161, 67, 98];
const showAvgSessionDurationChart = [140, 66, 64, 161, 67, 98];
const showPageViewChart = [140, 66, 64, 161];
const showCtrByVisits = [140, 66, 161, 64];
const visitsBycretiveType = [];
const showQualityVisitsByHva = [140, 64, 66, 161];
const showMapBreakDown = [140, 64, 66, 161];
const showAudienceByHva = [140, 66, 161, 64];
const showCostPerMin = [64, 66, 161];
const showEngagedSession = [];
const showSessionDurationByTime = [64, 66, 161, 140];
const showRepeatEngagementDistribution = [];
const showMixEngagedSession = [140, 161, 64, 66];
const WebMetrics = (props) => {
    const dispatch = useDispatch();
    const { resetToInitialState } = webMatrixActions;
    const {
        isLoading,
        isGeographyLoading,
        isCostApiLoading,
        isMultiUsageLoading,
        sessions,
        quality_visits,
        sessionsQualityVisits,
        qvr,
        bounce_rate,
        visits_broken_down_by_channels,
        quality_visits_broken_down_by_channels,
        visits_broken_down_by_creative_type,
        cost_per_quality_visit_by_channels,
        cost_per_quality_visit_by_channels_staticFR, // need to delete this line
        cost_per_quality_visit_by_channels_staticRE, // need to delete this line
        cost_per_quality_visit_by_channels_staticBA, // need to delete this line
        cost_per_quality_visit_by_channels_staticTP, // need to delete this line
        cost_per_quality_visit_by_channels_staticXE, // need to delete this line
        cost_per_quality_visit_by_channels_staticREMM, // need to delete this line
        cost_per_quality_visit_by_channels_staticZP, // need to delete this line
        avg_session_duration_data,
        page_views_data,
        ctr_broken_down_by_creative_type,
        qvr_by_hva_chart,
        audience_segement_by_hva,
        cost_per_30_seconds,
        engagedSessionData,
        session_duration_by_time,
        repeat_engagement_distribution,
        users_series_data: webMatrixUsersSeriesData,
    } = useSelector((state) => state.webMatrix);
    const { users_series_data, isLoading: dg_loading } = useSelector(
        (state) => state.digitalEngagement,
    );
    const { company } = useSelector((state) => state.company);
    // These companies take their New vs Returning series from the web matrix
    // slice (see safeNewVsReturningSeries below), so the digital engagement
    // response is never read on this page -- it is fetched, waited on, and
    // discarded. Measured on company 67 over three years: that call is 40-60s
    // while the four web matrix calls finish in 0.4-4.2s, and isFinalLoading
    // below was holding the whole page behind it.
    //
    // `company` is empty on the first render, before the company request
    // resolves, so this must be optional -- and it must sit after the selector
    // above, not before it.
    const usesDigitalUsersSeries =
        !NEW_VS_RETURNING_FROM_WEB_MATRIX.includes(company?.id);
    const { startDateRange, endDateRange } = useSelector(
        (state) => state.filter,
    );
    const { companyConfig } = useSelector((state) => state.adminSettings);
    const { value } = companyConfig;
    const isFinalLoading =
        isLoading ||
        isGeographyLoading ||
        isCostApiLoading ||
        isMultiUsageLoading ||
        (usesDigitalUsersSeries && dg_loading);

    useEffect(() => {
        if (company.id) {
            dispatch(getCompanyConfigurationByTag({ companyId: company.id }));
        }
    }, [company.id]);

    const isConfigReady =
        companyConfig && Object.keys(companyConfig).length > 0;

    useEffect(() => {
        if (company.id || isConfigReady) {
            dispatch(resetToInitialState());
            dispatch(
                getWebMatrixFalse({
                    companyId: company.id,
                    startRange: startDateRange,
                    endRange: endDateRange,
                }),
            );
            dispatch(
                getWebMatrixCostDataFalse({
                    companyId: company.id,
                    startRange: startDateRange,
                    endRange: endDateRange,
                }),
            );
            dispatch(
                getWebMatrixGeographyDataFalse({
                    companyId: company.id,
                    startRange: startDateRange,
                    endRange: endDateRange,
                }),
            );
            dispatch(
                getWebMatrixMultiUsageFalse({
                    companyId: company.id,
                    startRange: startDateRange,
                    endRange: endDateRange,
                }),
            );
            if (usesDigitalUsersSeries) dispatch(
                // This page reads only `users_series_data` from the digital
                // engagement slice, which the reducer builds from
                // new_users_chart_data / returning_users_chart_data. It never
                // touches the provider rows -- but omitting page/pageSize makes
                // the API return every provider: 41,905 rows and 33 MB for
                // company 67 over three years, which is 99.9% of the payload and
                // most of the response time (measured 55s unpaginated against
                // 16-18s with a page). Asking for a single row keeps the charts
                // identical and drops the rest.
                getDigitalEngagement({
                    companyId: company.id,
                    startRange: startDateRange,
                    endRange: endDateRange,
                    // page/pageSize: this page never renders the provider rows,
                    // and without them the API returns all 41,905 of them --
                    // 33 MB, 99.9% of the payload.
                    page: 1,
                    pageSize: 1,
                    //isUnified: value?.toLowerCase() === 'true' || false
                }),
            );
        }
    }, [company, startDateRange, endDateRange, isConfigReady]);

    const sessionsAreaChart = useMemo(() => {
        let combined = [];

        if (
            sessionsQualityVisits.chartSeries.length > 0 &&
            engagedSessionData.length > 0 &&
            showMixEngagedSession.includes(company.id)
        ) {
            combined = [
                ...sessionsQualityVisits.chartSeries,
                { name: 'Engaged Sessions', data: engagedSessionData },
            ];
        } else {
            combined = sessionsQualityVisits.chartSeries;
        }

        const filtered = combined.filter(
            (series) => Array.isArray(series.data) && series.data.length > 0,
        );

        return _.cloneDeep(filtered);
    }, [sessionsQualityVisits, engagedSessionData, company.id]);

    const safeNewVsReturningSeries = _.cloneDeep(
        NEW_VS_RETURNING_FROM_WEB_MATRIX.includes(company.id)
            ? webMatrixUsersSeriesData
            : users_series_data,
    );

    const handleChartOptions = (
        xAxisTitle = '',
        yAxisTitle = '',
        xAxisCategories = [],
        yAxisMax = null,
        colors,
        isPercentage = false,
        isCurrency = false,
    ) => {
        const options = {
            title: { text: null },
            subtitle: { text: null },
            chart: {
                backgroundColor: 'transparent',
                type: 'column',
            },
            colors: colors,
            mapNavigation: {
                enabled: false,
                enableButtons: false,
            },
            navigation: { buttonOptions: { enabled: false } },
            credits: { enabled: false },
            tooltip: {
                enabled: true,
                outside: true,
                followPointer: true,
                followTouchMove: true,
                formatter: function () {
                    const [fullMonth, year] = this.key.split(' '); // Split "December 2024" into "December" and "2024"
                    const shortMonth = new Date(
                        `${fullMonth} 1, 2000`,
                    ).toLocaleString('default', { month: 'short' }); // Convert to "Dec"
                    const pointValue = isPercentage
                        ? `${this.y}%`
                        : isCurrency
                          ? `$${this.y}`
                          : this.y; // Format value
                    return `<span style="font-size: 10px">${shortMonth} ${year}</span><br/>
                            <span style="font-size: 12px">${this.series.name}: <b>${pointValue}</b><br/></span>`;
                },
                style: { zIndex: 9999 },
            },
            legend: { enabled: false },
            dataLabels: { enabled: false },
            plotOptions: {
                column: {
                    stacking: 'normal',
                    borderRadius: 3,
                    maxPointWidth: 50,
                },
            },

            xAxis: {
                categories: xAxisCategories,
                title: { text: xAxisTitle },
                lineColor: 'transparent',
            },
            yAxis: {
                min: 0,
                title: { text: yAxisTitle },
                stackLabels: { enabled: false },
                lineColor: 'transparent',
                gridLineWidth: 0,
            },
        };

        if (yAxisMax) {
            return { ...options, yAxis: { ...options.yAxis, max: yAxisMax } };
        }

        if (isCurrency) {
            return {
                ...options,
                yAxis: {
                    ...options.yAxis,
                    labels: {
                        formatter: function () {
                            return '$' + this.value;
                        },
                    },
                },
            };
        }

        return options;
    };

    const handleStaticData = () => {
        let staticData = {};
        const showStatic = [41, 42, 3, 16, 37, 51, 45];
        if (company.id === 42) {
            staticData = {
                data: cost_per_quality_visit_by_channels_staticFR.dataSets,
                xAxisCategories:
                    cost_per_quality_visit_by_channels_staticFR.xAxisCategories,
                color: cost_per_quality_visit_by_channels_staticFR.colors,
            };
        }
        if (company.id === 41) {
            staticData = {
                data: cost_per_quality_visit_by_channels_staticRE.dataSets,
                xAxisCategories:
                    cost_per_quality_visit_by_channels_staticRE.xAxisCategories,
                color: cost_per_quality_visit_by_channels_staticRE.colors,
            };
        }
        if (company.id === 51) {
            staticData = {
                data: cost_per_quality_visit_by_channels_staticREMM.dataSets,
                xAxisCategories:
                    cost_per_quality_visit_by_channels_staticREMM.xAxisCategories,
                color: cost_per_quality_visit_by_channels_staticREMM.colors,
            };
        }
        if (company.id === 3) {
            staticData = {
                data: cost_per_quality_visit_by_channels_staticTP.dataSets,
                xAxisCategories:
                    cost_per_quality_visit_by_channels_staticTP.xAxisCategories,
                color: cost_per_quality_visit_by_channels_staticTP.colors,
            };
        }
        if (company.id === 16) {
            staticData = {
                data: cost_per_quality_visit_by_channels_staticBA.dataSets,
                xAxisCategories:
                    cost_per_quality_visit_by_channels_staticBA.xAxisCategories,
                color: cost_per_quality_visit_by_channels_staticBA.colors,
            };
        }
        if (company.id === 37) {
            staticData = {
                data: cost_per_quality_visit_by_channels_staticXE.dataSets,
                xAxisCategories:
                    cost_per_quality_visit_by_channels_staticXE.xAxisCategories,
                color: cost_per_quality_visit_by_channels_staticXE.colors,
            };
        }
        if (company.id === 45) {
            staticData = {
                data: cost_per_quality_visit_by_channels_staticZP.dataSets,
                xAxisCategories:
                    cost_per_quality_visit_by_channels_staticZP.xAxisCategories,
                color: cost_per_quality_visit_by_channels_staticZP.colors,
            };
        }
        return { staticData, showStaticData: showStatic.includes(company.id) };
    };

    const handleHideCostChart = () => {
        const companies = [66, 64, 140, 161]; //[37, 3, 16,40, 45, 42,41,51,53,59,233,64];
        return companies.includes(company.id);
    };
    const handleVisitChannelChart = () => {
        const companies = [40];
        return !companies.includes(company.id);
    };
    const handleHideBrokenChannelChart = () => {
        const companies = [40];
        return !companies.includes(company.id);
    };
    const areaChartYaxisLables = {
        session_quality_visits: {
            61: 'No. of Sessions, Quality Visits and Engaged Sessions',
            94: 'No. of Sessions, Quality Visits and Engaged Sessions',
            default: 'No. of Sessions and Quality Visits',
        },
        avg_session_duration: {
            61: 'Sessions Duration',
            94: 'Sessions Duration',
            default: 'Sessions Duration',
        },
        page_view: {
            61: 'Page views',
            94: 'Page views',
            default: 'Page views',
        },
        newUser_vs_retuningUser: {
            61: 'New User Vs Returning User',
            94: 'New User Vs Returning User',
            default: 'New User Vs Returning User',
        },
        engaged_session: {
            61: 'Engaged Sessions',
            94: 'Engaged Sessions',
            default: 'Engaged Sessions',
        },
    };

    const handleAreaChartOptions = (companyId, chartType) => {
        const options = _.cloneDeep(CREATIVE_NAME_CHART_OPTIONS);

        options.yaxis.title.text =
            areaChartYaxisLables[chartType]?.[companyId] ||
            areaChartYaxisLables[chartType]?.default;

        return options;
    };

    const chartHeaderItems = {
        66: {
            SessionAndQVR: 'Sessions, Quality Visits and Engaged Sessions',
            qvr: 'Quality Visits Rate (%)',
            bounce_rate: 'Bounce Rate (%)',
            qvrBrokenByChannel: 'Quality Visit Broken Down by Channels (%)',
            avg_session_duration: 'Average Session Duration (sec.)',
            newUser_vs_retuningUser: 'New User Vs Returning User',
            page_view: 'Page Views',
            cqvr: 'Cost Per First Quality Visit by Channels',
        },
        64: {
            SessionAndQVR: 'Sessions, Quality Visits and Engaged Sessions',
            qvr: 'Quality Visits Rate (%)',
            bounce_rate: 'Bounce Rate (%)',
            qvrBrokenByChannel: 'Quality Visit Broken Down by Channels (%)',
            avg_session_duration: 'Average Session Duration (sec.)',
            newUser_vs_retuningUser: 'New User Vs Returning User',
            page_view: 'Page Views',
            cqvr: 'Cost Per First Quality Visit by Channels',
        },
        161: {
            SessionAndQVR: 'Sessions, Quality Visits and Engaged Sessions',
            qvr: 'Quality Visits Rate (%)',
            bounce_rate: 'Bounce Rate (%)',
            qvrBrokenByChannel: 'Quality Visit Broken Down by Channels (%)',
            avg_session_duration: 'Average Session Duration (sec.)',
            newUser_vs_retuningUser: 'New User Vs Returning User',
            page_view: 'Page Views',
            cqvr: 'Cost Per First Quality Visit by Channels',
        },
        140: {
            SessionAndQVR: 'Sessions, Quality Visits and Engaged Sessions',
            qvr: 'Quality Visits Rate (%)',
            bounce_rate: 'Bounce Rate (%)',
            qvrBrokenByChannel: 'Quality Visit Broken Down by Channels (%)',
            avg_session_duration: 'Average Session Duration (sec.)',
            newUser_vs_retuningUser: 'New User Vs Returning User',
            page_view: 'Page Views',
            cqvr: 'Cost Per First Quality Visit by Channels',
        },
        default: {
            SessionAndQVR: 'Sessions And Quality Visits',
            qvr: 'Quality Visits Rate (%)',
            bounce_rate: 'Bounce Rate (%)',
            qvrBrokenByChannel: 'Quality Visits Broken Down by Channels (%)',
            avg_session_duration: 'Average Session Duration (sec.)',
            newUser_vs_retuningUser: 'New User Vs Returning User',
            page_view: 'Page Views',
            cqvr: 'Cost Per Quality Visit by Channels',
        },
    };

    const chartTitles =
        chartHeaderItems[company.id] || chartHeaderItems.default;
    const renderGraph = (
        metricsType,
        title,
        graph,
        isPercentage = false,
        isCurrency = false,
    ) => (
        <Grid item xs={6}>
            <Box className="web-metrics-graph-container">
                <Box className="padding-tb-24-rl-20">
                    <Typography className="text-md-semi-bold gray-900">
                        {isPercentage
                            ? title + '(%)'
                            : isCurrency
                              ? title + '($)'
                              : title}
                    </Typography>
                </Box>
                <Divider className="border-gray-200" />
                <Box className="padding-24px min-height-402px">
                    {isFinalLoading ? (
                        <Skeleton height={'400px'} variant="rectangular" />
                    ) : (
                        graph
                    )}
                </Box>
            </Box>
        </Grid>
    );

    return (
        <>
            <FilterBar />
            <Grid container spacing={2}>
                {/* Webmetrics */}
                <Grid item xs={12}>
                    <WebMetricsInfo />
                </Grid>
                <Grid item xs={12}>
                    <WebMetricsNote />
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={'24px'}>
                        {/* Sessions & Quality Visits */}
                        <Grid item xs={6}>
                            <Box className="web-metrics-graph-container h-100">
                                <Box className="padding-tb-24-rl-20">
                                    <Typography className="text-md-semi-bold gray-900">
                                        {chartTitles.SessionAndQVR}
                                    </Typography>
                                </Box>
                                <Divider className="border-gray-200" />
                                <Box className="padding-24px min-height-402px mt-40px">
                                    {isFinalLoading ? (
                                        <Skeleton
                                            height={'350px'}
                                            variant="rectangular"
                                        />
                                    ) : (
                                        <AreaChart
                                            chartOptions={handleAreaChartOptions(
                                                company.id,
                                                'session_quality_visits',
                                            )}
                                            chartSeries={sessionsAreaChart}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                        {/* new users vs returning users */}
                        {showUsersChart.includes(company.id) && (
                            <Grid item xs={6}>
                                <Box className="web-metrics-graph-container h-100">
                                    <Box className="padding-tb-24-rl-20">
                                        <Typography className="text-md-semi-bold gray-900">
                                            {'New User Vs Returning User'}
                                        </Typography>
                                    </Box>
                                    <Divider className="border-gray-200" />
                                    <Box className="padding-24px min-height-402px mt-40px">
                                        {isFinalLoading ? (
                                            <Skeleton
                                                height={'350px'}
                                                variant="rectangular"
                                            />
                                        ) : (
                                            <AreaChart
                                                chartOptions={handleAreaChartOptions(
                                                    company.id,
                                                    'newUser_vs_retuningUser',
                                                )}
                                                chartSeries={
                                                    safeNewVsReturningSeries
                                                }
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        )}
                        {hideBounceRateCompanies.includes(company.id) ? (
                            <Grid item xs={6}>
                                {/* Bounce Rate */}
                                <Box className="web-metrics-graph-container">
                                    <Box className="padding-tb-24-rl-20">
                                        <Typography className="text-md-semi-bold gray-900">
                                            Bounce Rate (%)
                                        </Typography>
                                    </Box>
                                    <Divider className="border-gray-200" />
                                    <Box className="padding-24px min-height-402px">
                                        {isFinalLoading ? (
                                            <Skeleton
                                                height={'400px'}
                                                variant="rectangular"
                                            />
                                        ) : (
                                            <HighchartBarChart
                                                chartOptions={handleChartOptions(
                                                    'Months',
                                                    'Bounce Rate',
                                                    bounce_rate.xAxisCategories,
                                                    null,
                                                    bounce_rate.colors,
                                                    true,
                                                    false,
                                                )}
                                                key={`chart-${bounce_rate.dataSets}-${company.id}`}
                                                seriesData={
                                                    bounce_rate.dataSets
                                                }
                                                seriesName="Bounce Rate"
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        ) : null}
                        {/* Visits Broken Down by Channels */}
                        {handleVisitChannelChart() &&
                            renderGraph(
                                'visit_broke_down_by_channels',
                                'Visits Broken Down by Channels',
                                <HighchartBarChart
                                    chartOptions={handleChartOptions(
                                        'Months',
                                        'Channel Visits',
                                        visits_broken_down_by_channels.xAxisCategories,
                                        100,
                                        visits_broken_down_by_channels.colors,
                                        true,
                                        false,
                                    )}
                                    seriesData={
                                        visits_broken_down_by_channels.dataSets
                                    }
                                />,
                                true,
                            )}
                        {/* Avg session duration  */}
                        {showAvgSessionDurationChart.includes(company.id) && (
                            <Grid item xs={6}>
                                <Box className="web-metrics-graph-container h-100">
                                    <Box className="padding-tb-24-rl-20">
                                        <Typography className="text-md-semi-bold gray-900">
                                            {'Average Session Duration (sec.)'}
                                        </Typography>
                                    </Box>
                                    <Divider className="border-gray-200" />
                                    <Box className="padding-24px min-height-402px mt-40px">
                                        {isFinalLoading ? (
                                            <Skeleton
                                                height={'400px'}
                                                variant="rectangular"
                                            />
                                        ) : (
                                            <AreaChart
                                                chartOptions={handleAreaChartOptions(
                                                    company.id,
                                                    'avg_session_duration',
                                                )}
                                                chartSeries={[
                                                    {
                                                        name: 'Avg session duration',
                                                        data: avg_session_duration_data,
                                                    },
                                                ]}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        )}
                        {/* Session Duration by Time */}
                        {showSessionDurationByTime.includes(company.id) &&
                            renderGraph(
                                'session_duration by time',
                                'Sessions Broken Down by Duration',
                                <HighchartBarChart
                                    chartOptions={handleChartOptions(
                                        'Months',
                                        'Session Duration',
                                        session_duration_by_time.xAxisCategories,
                                        null,
                                        session_duration_by_time.colors,
                                        false,
                                        false,
                                    )}
                                    seriesData={
                                        session_duration_by_time.dataSets
                                    }
                                />,
                                false,
                            )}
                        {/* Cost Per 30 Second of Session Visit */}
                        {showCostPerMin.includes(company.id) && (
                            <Grid item xs={6}>
                                <CPCChart
                                    chart_data={cost_per_30_seconds}
                                    isLoading={isFinalLoading}
                                />
                            </Grid>
                        )}
                        {/* Page View */}
                        {showPageViewChart.includes(company.id) && (
                            <Grid item xs={6}>
                                <Box className="web-metrics-graph-container h-100">
                                    <Box className="padding-tb-24-rl-20">
                                        <Typography className="text-md-semi-bold gray-900">
                                            {'Page Views'}
                                        </Typography>
                                    </Box>
                                    <Divider className="border-gray-200" />
                                    <Box className="padding-24px min-height-402px mt-40px">
                                        {isFinalLoading ? (
                                            <Skeleton
                                                height={'400px'}
                                                variant="rectangular"
                                            />
                                        ) : (
                                            <AreaChart
                                                chartOptions={handleAreaChartOptions(
                                                    company.id,
                                                    'page_view',
                                                )}
                                                chartSeries={[
                                                    {
                                                        name: 'Page View',
                                                        data: page_views_data,
                                                    },
                                                ]}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        )}
                        {/* QVR */}
                        {renderGraph(
                            'qvr',
                            'Quality Visits Rate',
                            <HighchartBarChart
                                chartOptions={handleChartOptions(
                                    'Months',
                                    'Quality Visit Rate',
                                    qvr.xAxisCategories,
                                    null,
                                    qvr.colors,
                                    true,
                                    false,
                                )}
                                seriesData={qvr.dataSets}
                                seriesName="Quality Visits Rate"
                            />,
                            true,
                        )}

                        {/* Quality Visits Broken Down by Actions  */}
                        {showQualityVisitsByHva.includes(company.id) &&
                            renderGraph(
                                'ctr_broken_down_by_creative_type',
                                'Quality Visits Broken Down by Actions',
                                <HighchartBarChart
                                    chartOptions={handleChartOptions(
                                        'Months',
                                        'Quality Visits',
                                        qvr_by_hva_chart.xAxisCategories,
                                        null,
                                        qvr_by_hva_chart.colors,
                                        false,
                                        false,
                                    )}
                                    seriesData={qvr_by_hva_chart.dataSets}
                                />,
                                false,
                            )}
                        {/* Quality Visits Broken Down by Audience Segments */}
                        {showAudienceByHva.includes(company.id) &&
                            renderGraph(
                                'audience_segement_by_hva',
                                'Quality Visits Broken Down by Audience Segments',
                                <HighchartBarChart
                                    chartOptions={handleChartOptions(
                                        'Months',
                                        'Audience',
                                        audience_segement_by_hva.xAxisCategories,
                                        100,
                                        audience_segement_by_hva.colors,
                                        true,
                                        false,
                                    )}
                                    seriesData={
                                        audience_segement_by_hva.dataSets
                                    }
                                />,
                                true,
                            )}

                        {/* Quality Visits Broken Down by Channels */}
                        {handleHideBrokenChannelChart() &&
                            renderGraph(
                                'quality_visit_broke_down_by_channels',
                                'Quality Visits Broken Down by Channels',
                                <HighchartBarChart
                                    chartOptions={handleChartOptions(
                                        'Months',
                                        'Quality Channel Visits',
                                        quality_visits_broken_down_by_channels.xAxisCategories,
                                        100,
                                        quality_visits_broken_down_by_channels.colors,
                                        true,
                                        false,
                                    )}
                                    seriesData={
                                        quality_visits_broken_down_by_channels.dataSets
                                    }
                                />,
                                true,
                            )}
                        {/* Cost Per Quality Visit by Channels */}
                        {
                            // need to delete thus condition based rendering and remove above one
                            handleHideCostChart() &&
                                (handleStaticData().showStaticData
                                    ? renderGraph(
                                          'cost_per_quality_visit_by_channel',
                                          'Cost Per Quality Visit by Channels',
                                          <HighchartBarChart
                                              chartOptions={handleChartOptions(
                                                  'Months',
                                                  'Quality Cost Visits',
                                                  handleStaticData().staticData
                                                      .xAxisCategories,
                                                  null,
                                                  handleStaticData().staticData
                                                      .color,
                                                  false,
                                                  true,
                                              )}
                                              seriesData={
                                                  handleStaticData().staticData
                                                      .data
                                              }
                                          />,
                                          false,
                                          true,
                                      )
                                    : renderGraph(
                                          'cost_per_quality_visit_by_channel',
                                          chartTitles.cqvr,
                                          <HighchartBarChart
                                              chartOptions={handleChartOptions(
                                                  'Months',
                                                  'Quality Cost Visits',
                                                  cost_per_quality_visit_by_channels.xAxisCategories,
                                                  null,
                                                  cost_per_quality_visit_by_channels.colors,
                                                  false,
                                                  true,
                                              )}
                                              seriesData={
                                                  company.id === 40
                                                      ? []
                                                      : cost_per_quality_visit_by_channels.dataSets
                                              } // need to delete this
                                          />,
                                          false,
                                          true,
                                      ))
                        }

                        {/* CTR broken down by Creative Types */}
                        {showCtrByVisits.includes(company.id) &&
                            renderGraph(
                                'ctr_broken_down_by_creative_type',
                                'CTR Broken Down by Creative Types',
                                <HighchartBarChart
                                    chartOptions={handleChartOptions(
                                        'Months',
                                        'CTR',
                                        ctr_broken_down_by_creative_type.xAxisCategories,
                                        100,
                                        ctr_broken_down_by_creative_type.colors,
                                        true,
                                        false,
                                    )}
                                    seriesData={
                                        ctr_broken_down_by_creative_type.dataSets
                                    }
                                />,
                                true,
                            )}
                        {/* Visits Broken Down by Creative Type */}
                        {visitsBycretiveType.includes(company.id) &&
                            renderGraph(
                                'visit_broke_down_by_creative_type',
                                'Visits Broken Down by Creative Type',
                                <HighchartBarChart
                                    chartOptions={handleChartOptions(
                                        'Months',
                                        'Creative Type',
                                        visits_broken_down_by_creative_type.xAxisCategories,
                                        100,
                                        visits_broken_down_by_creative_type?.colors,
                                        true,
                                        false,
                                    )}
                                    seriesData={
                                        visits_broken_down_by_creative_type.dataSets
                                    }
                                />,
                                true,
                            )}
                        {/* Engaged Session  */}
                        {showEngagedSession.includes(company.id) && (
                            <Grid item xs={6}>
                                <Box className="web-metrics-graph-container h-100">
                                    <Box className="padding-tb-24-rl-20">
                                        <Typography className="text-md-semi-bold gray-900">
                                            Engaged Sessions
                                        </Typography>
                                    </Box>
                                    <Divider className="border-gray-200" />
                                    <Box className="padding-24px min-height-402px mt-40px">
                                        {isFinalLoading ? (
                                            <Skeleton
                                                height={'400px'}
                                                variant="rectangular"
                                            />
                                        ) : (
                                            <AreaChart
                                                chartOptions={handleAreaChartOptions(
                                                    company.id,
                                                    'engaged_session',
                                                )}
                                                chartSeries={[
                                                    {
                                                        name: 'Engaged Sessions',
                                                        data: engagedSessionData,
                                                    },
                                                ]}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        )}

                        {/* Repeat Engagement Distribution */}
                        {showRepeatEngagementDistribution.includes(
                            company.id,
                        ) &&
                            renderGraph(
                                'repeat_engagement_distribution',
                                'Repeat Engagement Distribution',
                                <HighchartBarChart
                                    chartOptions={handleChartOptions(
                                        'Months',
                                        'Repeat Engagement Distribution',
                                        repeat_engagement_distribution.xAxisCategories,
                                        null,
                                        repeat_engagement_distribution.colors,
                                        false,
                                        false,
                                    )}
                                    seriesData={
                                        repeat_engagement_distribution.dataSets
                                    }
                                />,
                                false,
                            )}
                        {showMapBreakDown.includes(company.id) && (
                            <Grid item xs={12}>
                                <MapBreakDown />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default WebMetrics;
